import { useCallback } from 'react';
import type { CanvasObject } from '../types';

interface UseClipMaskRelationsProps {
  objects: Map<string, CanvasObject>;
  zOrder: string[];
  updateObject: (id: string, updater: (obj: CanvasObject) => CanvasObject) => void;
}

/**
 * 클리핑 마스크 관계 관리 훅
 * - zOrder 기반으로 자동 관계 설정
 * - toggleClipMask: 수동 토글
 */
export function useClipMaskRelations({
  objects,
  zOrder,
  updateObject
}: UseClipMaskRelationsProps) {
  /**
   * 클리핑 마스크 관계 자동 업데이트
   * zOrder를 기반으로 바로 아래 레이어를 마스크로 사용
   */
  const updateClipMaskRelations = useCallback(() => {
    // zOrder를 역순으로 순회 (위에서 아래로)
    for (let i = zOrder.length - 1; i >= 0; i--) {
      const objId = zOrder[i];
      const obj = objects.get(objId);
      if (!obj) continue;
      
      // 바로 아래 레이어 찾기
      let clipMaskTarget: CanvasObject | null = null;
      
      if (i > 0) {
        const immediateBelowId = zOrder[i - 1];
        const immediateBelow = objects.get(immediateBelowId);
        
        if (immediateBelow) {
          // 바로 아래 레이어가 클리핑되지 않았으면 그것을 타겟으로
          if (!immediateBelow.isClipped && !immediateBelow.clipMaskId) {
            clipMaskTarget = immediateBelow;
          } else if (immediateBelow.isClipped && immediateBelow.clipMaskId) {
            // 바로 아래 레이어가 클리핑되어 있으면, 그 아래의 클리핑되지 않은 레이어를 찾음
            const clipTargetIdx = zOrder.indexOf(immediateBelow.clipMaskId);
            if (clipTargetIdx >= 0) {
              clipMaskTarget = objects.get(zOrder[clipTargetIdx]) || null;
            }
          }
        }
      }
      
      if (clipMaskTarget) {
        // 클리핑 마스크 적용 또는 업데이트
        if (obj.clipMaskId !== clipMaskTarget.id || !obj.isClipped) {
          updateObject(objId, (o) => ({
            ...o,
            clipMaskId: clipMaskTarget!.id,
            isClipped: true
          }));
        }
      } else if (obj.isClipped) {
        // 클리핑 마스크 대상이 없으면 해제
        updateObject(objId, (o) => ({
          ...o,
          clipMaskId: undefined,
          isClipped: false
        }));
      }
    }
  }, [objects, zOrder, updateObject]);

  /**
   * 클리핑 마스크 토글
   * 선택된 객체들을 바로 아래 레이어로 클리핑
   */
  const toggleClipMask = useCallback((selectedIds: string[]) => {
    if (selectedIds.length < 1) return;
    
    // 선택된 객체들 중 zOrder에서 가장 아래에 있는 것 찾기
    let bottomSelectedIdx = -1;
    let bottomSelectedId = '';
    
    for (let i = 0; i < zOrder.length; i++) {
      if (selectedIds.includes(zOrder[i])) {
        bottomSelectedIdx = i;
        bottomSelectedId = zOrder[i];
        break;
      }
    }
    
    if (bottomSelectedIdx === -1) return;
    
    // 바로 아래 레이어 찾기
    let clipMaskTarget: CanvasObject | null = null;
    if (bottomSelectedIdx > 0) {
      const belowId = zOrder[bottomSelectedIdx - 1];
      const belowObj = objects.get(belowId);
      if (belowObj && !belowObj.isClipped && !belowObj.clipMaskId) {
        clipMaskTarget = belowObj;
      }
    }
    
    if (!clipMaskTarget) {
      // 클리핑 마스크 대상이 없으면 모든 선택된 객체의 클리핑 해제
      selectedIds.forEach(id => {
        const obj = objects.get(id);
        if (obj && obj.isClipped) {
          updateObject(id, (o) => ({
            ...o,
            clipMaskId: undefined,
            isClipped: false
          }));
        }
      });
      return;
    }
    
    // clipMaskTarget이 null이 아님을 보장 (위에서 early return)
    const target = clipMaskTarget;
    
    // 선택된 객체들에 클리핑 마스크 적용/해제
    selectedIds.forEach(id => {
      const obj = objects.get(id);
      if (!obj) return;
      
      if (obj.clipMaskId === target.id && obj.isClipped) {
        // 이미 이 레이어로 클리핑이 적용되어 있으면 해제
        updateObject(id, (o) => ({
          ...o,
          clipMaskId: undefined,
          isClipped: false
        }));
      } else {
        // 클리핑 적용
        updateObject(id, (o) => ({
          ...o,
          clipMaskId: target.id,
          isClipped: true
        }));
      }
    });
  }, [objects, zOrder, updateObject]);

  return {
    updateClipMaskRelations,
    toggleClipMask
  };
}

